import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Scale, FileText } from "lucide-react";
import { useLang } from "@/lib/i18n";

const FR = {
  back: "Retour à l'accueil",
  effective: "Dernière mise à jour : Février 2026",

  cgu: {
    title: "Conditions Générales d'Utilisation",
    icon: Scale,
    sections: [
      {
        h: "1. Objet",
        p: "Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme Mazra3ti (ci-après « le Service »), service en ligne d'aide à la gestion d'élevage, édité par Haithem Sakkal. Toute utilisation du Service implique l'acceptation pleine et entière des présentes CGU.",
      },
      {
        h: "2. Description du Service",
        p: "Mazra3ti est une application web et mobile destinée aux éleveurs leur permettant de suivre leurs animaux, événements sanitaires, événements de reproduction, stocks d'alimentation et indicateurs économiques. L'accès au Service nécessite la création d'un compte et le respect des paliers tarifaires en vigueur.",
      },
      {
        h: "3. Inscription et compte utilisateur",
        p: "L'utilisateur s'engage à fournir des informations exactes lors de son inscription et à maintenir ses identifiants confidentiels. Tout accès au compte est réputé effectué par l'utilisateur. Mazra3ti se réserve le droit de suspendre ou supprimer tout compte en cas d'usage frauduleux ou contraire aux CGU.",
      },
      {
        h: "4. Essai gratuit & abonnement",
        p: "Tout nouvel utilisateur bénéficie de 30 jours d'essai gratuit sans engagement. À l'issue de cette période, l'accès au Service nécessite la souscription d'un abonnement payant (Essentiel, Pro ou Domaine). Les tarifs sont consultables sur la page Tarifs et peuvent être modifiés moyennant un préavis de 30 jours. Aucun remboursement n'est dû pour la période d'essai non consommée.",
      },
      {
        h: "5. Service de conseil technique",
        p: "Les utilisateurs des plans Pro et Domaine peuvent solliciter Seifeddine Ben Rejeb, ingénieur agronome et animalier, dans la limite de quotas mensuels (5 questions WhatsApp + 5 demandes de rappel/mois pour le plan Pro). IMPORTANT : Seifeddine n'est pas vétérinaire diplômé. Ses conseils portent exclusivement sur la conduite d'élevage, la nutrition animale, la reproduction technique et les bonnes pratiques. En aucun cas il ne peut prescrire de médicaments, établir un diagnostic médical ou se substituer à un vétérinaire. Pour toute pathologie, blessure ou prescription, l'utilisateur s'engage à consulter un vétérinaire diplômé inscrit à l'Ordre National des Vétérinaires de Tunisie (ONVT) ou de tout autre pays compétent.",
      },
      {
        h: "6. Limitation de responsabilité",
        p: "Mazra3ti, son éditeur et ses collaborateurs ne pourront en aucun cas être tenus responsables : (a) des décisions prises par l'utilisateur sur la base des données ou conseils fournis par le Service ; (b) des conséquences sanitaires, économiques ou autres affectant les animaux ou l'exploitation de l'utilisateur ; (c) des interruptions de service, pertes de données ou dysfonctionnements liés à des cas de force majeure, pannes d'infrastructure tierce ou problèmes de connexion ; (d) de l'usage fait par l'utilisateur des informations transmises. L'utilisateur reste seul responsable de la conduite de son exploitation et des décisions médicales concernant ses animaux.",
      },
      {
        h: "7. Propriété des données",
        p: "Les données saisies par l'utilisateur dans le Service (animaux, événements, stocks, etc.) restent sa propriété exclusive. Mazra3ti s'engage à ne pas les divulguer à des tiers sans son consentement explicite, sauf obligation légale. L'utilisateur peut à tout moment exporter ses données depuis son tableau de bord ou en demander la suppression définitive par e-mail à contact@mazra3ti.tn.",
      },
      {
        h: "8. Propriété intellectuelle",
        p: "L'ensemble des éléments composant le Service (code source, logo, design, textes, illustrations) est la propriété exclusive de l'éditeur. Toute reproduction, modification ou exploitation non autorisée est interdite et passible de poursuites.",
      },
      {
        h: "9. Sécurité",
        p: "Les données sont transmises de manière chiffrée (HTTPS), les mots de passe sont hachés (algorithme bcrypt) et l'accès aux comptes peut être protégé par authentification à deux facteurs (2FA). Toutefois, aucune méthode de transmission ou de stockage électronique n'est totalement sécurisée ; l'éditeur ne peut garantir une sécurité absolue.",
      },
      {
        h: "10. Résiliation",
        p: "L'utilisateur peut résilier son abonnement à tout moment depuis son espace client. La résiliation prend effet à la fin de la période payée. L'éditeur se réserve le droit de résilier unilatéralement tout compte en cas de violation des CGU, de fraude ou d'impayé.",
      },
      {
        h: "11. Modification des CGU",
        p: "L'éditeur peut modifier les CGU à tout moment. Les utilisateurs seront informés des modifications substantielles par e-mail au moins 15 jours avant leur entrée en vigueur. La poursuite de l'utilisation du Service après cette date vaut acceptation des nouvelles CGU.",
      },
      {
        h: "12. Loi applicable & juridiction",
        p: "Les présentes CGU sont régies par le droit tunisien. Tout litige relatif à leur interprétation ou exécution sera soumis aux tribunaux compétents de Tunis, après tentative de résolution amiable.",
      },
    ],
  },

  mentions: {
    title: "Mentions Légales",
    icon: FileText,
    sections: [
      {
        h: "Éditeur du site",
        p: "Le site mazra3ti.tn est édité par Haithem Sakkal, fondateur de Mazra3ti, en qualité d'entrepreneur individuel.\n\nAdresse postale : 8 place Jean Grandel, 92230 Gennevilliers, France\nContact e-mail : contact@mazra3ti.tn\nTéléphone / WhatsApp : +33 7 77 00 84 33",
      },
      {
        h: "Directeur de la publication",
        p: "Haithem Sakkal, fondateur et responsable éditorial de Mazra3ti.",
      },
      {
        h: "Hébergement",
        p: "L'application est hébergée sur une infrastructure cloud sécurisée (Emergent Labs et ses sous-traitants techniques). Les communications entre l'utilisateur et le Service sont chiffrées en HTTPS.",
      },
      {
        h: "Nom de domaine",
        p: "Le domaine mazra3ti.tn est enregistré via OVH SAS, dans le respect des règles de nommage édictées par l'Agence Tunisienne d'Internet (ATI).",
      },
      {
        h: "Propriété intellectuelle",
        p: "L'ensemble du contenu de ce site — textes, graphismes, logos, icônes, code, design — est protégé par le Code de la propriété intellectuelle et appartient à Haithem Sakkal, sauf mention contraire. Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.",
      },
      {
        h: "Crédits photographiques",
        p: "Les photographies illustrant les sections « Éleveurs » et « Animaux » proviennent de banques d'images libres de droits (Unsplash). Les photos de Haithem Sakkal et Seifeddine Ben Rejeb sont publiées avec leur consentement explicite.",
      },
      {
        h: "Cookies",
        p: "Le Service utilise uniquement des cookies strictement nécessaires à son fonctionnement (session, authentification). Aucun cookie publicitaire ou de tracking tiers n'est déposé.",
      },
      {
        h: "Signaler un problème",
        p: "Pour toute question relative au contenu, à la sécurité ou aux données, contactez : contact@mazra3ti.tn",
      },
    ],
  },

  privacy: {
    title: "Politique de Confidentialité",
    icon: Shield,
    sections: [
      {
        h: "1. Données collectées",
        p: "Mazra3ti collecte uniquement les données nécessaires au fonctionnement du Service : nom, e-mail, mot de passe chiffré, filière d'élevage, plan d'abonnement, données relatives aux animaux et événements saisis par l'utilisateur, journaux de connexion, et données techniques (adresse IP, type de navigateur) à des fins de sécurité.",
      },
      {
        h: "2. Finalités",
        p: "Les données sont utilisées exclusivement pour : (a) fournir et améliorer le Service ; (b) communiquer avec l'utilisateur (notifications, support, relances d'inactivité) ; (c) traiter les paiements via le prestataire Stripe ; (d) assurer la sécurité de la plateforme ; (e) répondre aux obligations légales et fiscales.",
      },
      {
        h: "3. Conservation",
        p: "Les données du compte sont conservées tant que le compte est actif. En cas de suppression du compte, les données personnelles sont supprimées sous 30 jours, à l'exception des données nécessaires au respect d'obligations légales (facturation, comptabilité — 10 ans).",
      },
      {
        h: "4. Partage avec des tiers",
        p: "Aucune donnée personnelle n'est vendue ou louée à des tiers. Les données peuvent être partagées avec : Stripe (paiements), Twilio (notifications WhatsApp — lorsque activé), l'hébergeur cloud, et toute autorité judiciaire ou administrative sur réquisition légale.",
      },
      {
        h: "5. Droits de l'utilisateur",
        p: "Conformément à la loi tunisienne 2004-63 relative à la protection des données personnelles et au RGPD européen pour les utilisateurs concernés, l'utilisateur dispose des droits suivants : accès, rectification, effacement, portabilité, opposition. Ces droits peuvent être exercés en écrivant à contact@mazra3ti.tn (réponse sous 30 jours).",
      },
      {
        h: "6. Sécurité",
        p: "Les mots de passe sont hachés avec bcrypt, les communications sont chiffrées en HTTPS, l'authentification à deux facteurs est disponible, et un système anti-bruteforce protège les comptes. Les sauvegardes sont effectuées quotidiennement.",
      },
      {
        h: "7. Cookies",
        p: "Mazra3ti n'utilise que des cookies fonctionnels (session, authentification). Aucun cookie publicitaire ou de profilage n'est utilisé.",
      },
      {
        h: "8. Contact du responsable des données",
        p: "Pour toute question relative à vos données personnelles, contactez : contact@mazra3ti.tn (Haithem Sakkal, responsable du traitement).",
      },
    ],
  },
};

const AR = {
  back: "العودة إلى الصفحة الرئيسية",
  effective: "آخر تحديث: فيفري 2026",
  cgu: {
    title: "الشروط العامة للاستخدام",
    icon: Scale,
    sections: [
      { h: "1. الموضوع", p: "تنظم هذه الشروط العامة للاستخدام الوصول إلى منصة Mazra3ti واستخدامها. كل استخدام للخدمة يستوجب القبول الكامل لهذه الشروط." },
      { h: "2. وصف الخدمة", p: "Mazra3ti تطبيق ويب وجوال موجّه للمربّين لإدارة قطعانهم: الحيوانات، الصحة، التكاثر، التغذية، التحليلات الاقتصادية." },
      { h: "3. التسجيل والحساب", p: "يلتزم المستخدم بتقديم معلومات دقيقة عند التسجيل والحفاظ على سرية معرفاته. أي استخدام للحساب يُعتبر صادرًا عن المستخدم." },
      { h: "4. التجربة المجانية والاشتراك", p: "يستفيد كل مستخدم جديد من 30 يومًا تجربة مجانية. بعد ذلك يلزم الاشتراك في إحدى الباقات (Essentiel / Pro / Domaine)." },
      { h: "5. خدمة الاستشارة التقنية", p: "هام: سيف الدين بن رجب مهندس فلاحي وليس طبيبًا بيطريًا. نصائحه تقنية فقط. لأي مرض أو وصفة، يجب استشارة طبيب بيطري مرخّص." },
      { h: "6. تحديد المسؤولية", p: "لا يمكن تحميل Mazra3ti أو محرريه أي مسؤولية عن القرارات التي يتخذها المستخدم بناءً على بياناته أو نصائح فريقها." },
      { h: "7. ملكية البيانات", p: "البيانات التي يدخلها المستخدم تبقى ملكه الخاص. يمكنه تصديرها أو طلب حذفها في أي وقت." },
      { h: "8. القانون المعمول به", p: "تخضع هذه الشروط للقانون التونسي وكل نزاع يُحال إلى المحاكم المختصة بتونس." },
    ],
  },
  mentions: {
    title: "البيانات القانونية",
    icon: FileText,
    sections: [
      { h: "ناشر الموقع", p: "Haithem Sakkal — 8 place Jean Grandel, 92230 Gennevilliers, France\nالبريد: contact@mazra3ti.tn\nالهاتف: +33 7 77 00 84 33" },
      { h: "مدير النشر", p: "Haithem Sakkal، مؤسس ومسؤول التحرير." },
      { h: "الاستضافة", p: "التطبيق مستضاف على بنية تحتية سحابية آمنة. الاتصالات مشفّرة بـ HTTPS." },
      { h: "اسم النطاق", p: "اسم النطاق mazra3ti.tn مسجّل لدى OVH وفق قواعد ATI تونس." },
      { h: "الملكية الفكرية", p: "كل محتوى الموقع محمي بقانون الملكية الفكرية." },
    ],
  },
  privacy: {
    title: "سياسة الخصوصية",
    icon: Shield,
    sections: [
      { h: "البيانات المجمّعة", p: "نجمع فقط البيانات الضرورية: الاسم، البريد، كلمة المرور المشفّرة، بيانات الحيوانات والأحداث، وسجلات الاتصال." },
      { h: "الأغراض", p: "تُستخدم البيانات لتقديم الخدمة، التواصل، معالجة المدفوعات، الأمن، والامتثال للالتزامات القانونية." },
      { h: "حقوق المستخدم", p: "وفق القانون التونسي 2004-63 و GDPR: الوصول، التصحيح، الحذف، النقل، الاعتراض. للتواصل: contact@mazra3ti.tn" },
      { h: "الأمن", p: "كلمات المرور مشفّرة بـ bcrypt، الاتصالات HTTPS، المصادقة الثنائية متاحة، وحماية ضد الهجمات." },
    ],
  },
};

export default function LegalPage({ doc }) {
  const { lang } = useLang();
  const T = lang === "ar" ? AR : FR;
  const d = T[doc];
  if (!d) return null;
  const Icon = d.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" dir={lang === "ar" ? "rtl" : "ltr"}>
      <header className="border-b border-[#E2E4DF] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="flex items-center gap-2 text-sm font-semibold text-[#1C3A27] hover:text-[#E05A3D]"
          >
            <ArrowLeft size={16} className={lang === "ar" ? "rotate-180" : ""} />
            {T.back}
          </Link>
          <span className="font-display text-xl font-black text-[#1C3A27]">Mazra3ti</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1C3A27] grid place-items-center">
            <Icon size={22} className="text-[#E05A3D]" />
          </div>
          <h1 data-testid={`legal-title-${doc}`} className="font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            {d.title}
          </h1>
        </div>
        <p className="text-sm text-[#5C6B5C] mb-10">{T.effective}</p>

        <div className="space-y-8">
          {d.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${doc}-${i}`}>
              <h2 className="font-display text-xl md:text-2xl font-bold text-[#1C3A27] mb-3">{s.h}</h2>
              <p className="text-base text-[#1C3A27]/85 leading-relaxed whitespace-pre-line">{s.p}</p>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#E2E4DF] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-8 flex flex-wrap gap-6 text-sm text-[#5C6B5C]">
          <Link to="/legal/cgu" className="hover:text-[#1C3A27]">CGU</Link>
          <Link to="/legal/mentions" className="hover:text-[#1C3A27]">Mentions légales</Link>
          <Link to="/legal/privacy" className="hover:text-[#1C3A27]">Confidentialité</Link>
          <span className="ms-auto">© 2026 Mazra3ti</span>
        </div>
      </footer>
    </div>
  );
}
